import styled, { css } from 'styled-components'

import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { StIconBadge } from '../IconBadge.styled'

import type { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ComponentPropsWithoutRef } from 'react'

/* ------------------------------------------------------------------------------------------------- *
 * LocationCard
 * ------------------------------------------------------------------------------------------------- */

type LocationCardRootProps = ComponentPropsWithoutRef<'div'>

const LocationCardRoot = ({ children, ...props }: LocationCardRootProps) => (
  <StLocationCard {...props}>{children}</StLocationCard>
)

/* ------------------------------------------------------------------------------------------------- *
 * LocationCard.Navigate
 * ------------------------------------------------------------------------------------------------- */

// The navigable card is its own part rather than the root growing a button when it is handed an
// onClick: which element a call-site gets should not depend on which props it passed.
type LocationCardNavigateProps = ComponentPropsWithoutRef<'button'>

const LocationCardNavigate = ({ children, ...props }: LocationCardNavigateProps) => (
  <StLocationNavigate type='button' {...props}>
    {children}
  </StLocationNavigate>
)

/* ------------------------------------------------------------------------------------------------- *
 * LocationCard.Name
 * ------------------------------------------------------------------------------------------------- */

// TODO: the text parts are typed to 'span' instead of the Span they wrap, so a call-site cannot
// pass type or color. Widen to ComponentPropsWithoutRef<typeof Span> when one needs to.
type LocationCardNameProps = ComponentPropsWithoutRef<'span'>

const LocationCardName = ({ children, ...props }: LocationCardNameProps) => (
  <StText type='BodyLargeRegular' {...props}>
    {children}
  </StText>
)

/* ------------------------------------------------------------------------------------------------- *
 * LocationCard.City
 * ------------------------------------------------------------------------------------------------- */

type LocationCardCityProps = ComponentPropsWithoutRef<'span'>

const LocationCardCity = ({ children, ...props }: LocationCardCityProps) => (
  <Span type='BodyLargeBold' {...props}>
    {children}
  </Span>
)

/* ------------------------------------------------------------------------------------------------- *
 * LocationCard.Icon
 * ------------------------------------------------------------------------------------------------- */

type LocationCardIconProps = ComponentPropsWithoutRef<typeof FontAwesomeIcon>

const LocationCardIcon = (props: LocationCardIconProps) => <StIconBadge {...props} />

/* ------------------------------------------------------------------------------------------------- *
 * LocationCard.Address
 * ------------------------------------------------------------------------------------------------- */

type LocationCardAddressProps = ComponentPropsWithoutRef<'span'>

const LocationCardAddress = ({ children, ...props }: LocationCardAddressProps) => (
  <StText type='BodyLargeRegular' {...props}>
    {children}
  </StText>
)

const LocationCard = Object.assign(LocationCardRoot, {
  Navigate: LocationCardNavigate,
  Name: LocationCardName,
  City: LocationCardCity,
  Address: LocationCardAddress,
  Icon: LocationCardIcon,
})

export { LocationCard }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

const cardBoxCSS = css`
  ${({ theme }) => `
    padding: ${theme.UI.SpacingPx.Space5};
    border: 1px solid ${theme.Components.Card.Border};
    border-radius: ${theme.UI.BorderRadius.Large}px;
    background-color: ${theme.Components.Card.Fill};
  `}
`

const StLocationCard = styled.div`
  ${cardBoxCSS}
`

const StLocationNavigate = styled.button`
  ${cardBoxCSS}

  ${({ theme }) => `
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: ${theme.UI.SpacingPx.Space3};
    width: 100%;
    text-align: left;
    font: inherit;
    color: inherit;
    cursor: pointer;
  `}
`

const StText = styled(Span)`
  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
`
